import { useRef, useState } from 'react';
import { forwardRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { formLabelClasses, TextField } from '@mui/material';
import { useField } from 'formik';

import HelperText from './HelperText';

const NumberFormatCustom = forwardRef(({ onChange, id, readOnly, disabled, ...rest }, ref) => {
	NumberFormatCustom.displayName = 'NumberFormat';
	const [field] = useField(id);

	const attrs = {
		decimalScale: 2,
		thousandSeparator: ',',
		fixedDecimalScale: true,
		...rest
	};

	return (
		<NumericFormat
			{...attrs}
			getInputRef={ref}
			onValueChange={e => onChange(e, field)}
			readOnly={readOnly ? true : ''}
			disabled={readOnly ? true : disabled}
			autoComplete="off"
			prefix="$"
		/>
	);
});

export default function CurrencyField({ name, onChange, onKeyDown, margin, required, predicted = undefined, helperText, sx, hideBorder = false, ...props }) {
	const [field, meta, helper] = useField(name);
	const ref = useRef();

	let filterValue = undefined;

	if (props.filterState) {
		const filterMap = {
			Start: props.filterState.minValue,
			End: props.filterState.maxValue
		};

		filterValue = filterMap[props.placeholder] || props.filterState.value;
	}

	const handleChange = event => {
		if (onKeyDown) ref.current.addEventListener('keydown', onKeyDown);
		if (onChange) onChange(event, helper);

		if (!event) {
			helper.setValue('');

			return;
		}

		helper.setValue(event.value);
	};

	return (
		<TextField
			{...props}
			fullWidth
			placeholder="$0.00"
			inputRef={ref}
			error={!!meta.error}
			onChange={handleChange}
			value={props.filterState ? filterValue : field.value}
			margin={margin ?? 'normal'}
			InputProps={{
				className: 'field',
				inputComponent: NumberFormatCustom,
				...props.InputProps
			}}
			sx={{
				...sx,
				'& .MuiOutlinedInput-notchedOutline': {
					// if predicted exists, field is not blurred, and field value is equal to predicted, change border color to warning
					...(predicted && field.value == predicted ? { borderColor: 'common.warning' } : {})
				},
				'& .MuiInputBase-root.Mui-disabled, .MuiInputBase-input.Mui-disabled': {
					...(hideBorder
						? {
								color: 'unset',
								'-webkit-text-fill-color': 'unset',
								background: 'unset',
								'& fieldset': { display: 'none' }
							}
						: {})
				}
			}}
			required={required}
			helperText={<HelperText meta={meta} required={required} helperText={helperText} />}
		/>
	);
}
